/**
 * Public single-blog view — premium light theme, mirrors PublicBlogPage styling.
 * Handles both HTML (new inbound articles) and Markdown (legacy blogs).
 */
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, Eye, Heart, Tag, Calendar, ArrowLeft, ArrowRight,
  Share2, RefreshCw, BookOpen, Sparkles,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/blogs`;

// Cheap detector: HTML content will start with `<` after trim.
const looksLikeHtml = (s) => typeof s === "string" && s.trim().startsWith("<");

// Minimal markdown → HTML for legacy posts.
function markdownToHtml(md) {
  if (!md) return "";
  return md
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>')
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`(.*?)`/g, '<code>$1</code>')
    .split(/\n{2,}/)
    .map((block) => (/^<(h\d|ul|ol|pre|blockquote)/.test(block.trim()) ? block : `<p>${block}</p>`))
    .join("\n");
}

export default function BlogViewPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/slug/${slug}`);
        if (cancelled) return;
        setBlog(data);
        if (data.category) {
          try {
            const rel = await axios.get(
              `${API}/list?status=published&category=${encodeURIComponent(data.category)}&limit=4`,
            );
            if (!cancelled) {
              setRelated((rel.data.blogs || []).filter((b) => b.slug !== slug).slice(0, 3));
            }
          } catch { /* related is non-critical */ }
        }
      } catch {
        if (!cancelled) {
          toast.error("Article not found");
          navigate("/blogs");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  const contentHtml = useMemo(() => {
    if (!blog?.content) return "";
    return looksLikeHtml(blog.content) ? blog.content : markdownToHtml(blog.content);
  }, [blog]);

  const handleLike = async () => {
    if (!blog?.id) { toast.info("Likes coming soon for this article"); return; }
    try {
      const { data } = await axios.post(`${API}/${blog.id}/like`);
      setBlog({ ...blog, likes: data.likes });
    } catch { /* silent */ }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: blog.title, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
        <PremiumNav />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#A855F7]" />
        </div>
      </div>
    );
  }

  if (!blog) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      {/* Article header */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/blogs")}
            className="text-neutral-500 hover:text-[#7C3AED] -ml-3 mb-6"
            data-testid="blog-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All articles
          </Button>

          <div className="flex items-center gap-2 mb-4">
            {blog.category && (
              <Badge className="bg-[#A855F7]/10 text-[#7C3AED] hover:bg-[#A855F7]/15 border-transparent">
                {blog.category}
              </Badge>
            )}
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
              <Sparkles className="w-3 h-3" /> MarkeeMix Blog
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#111111] tracking-tight leading-[1.1]">
            {blog.title}
          </h1>
          {blog.excerpt && !looksLikeHtml(blog.excerpt) && (
            <p className="text-base text-neutral-600 mt-4 leading-relaxed">{blog.excerpt}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-neutral-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(blog.published_at || blog.created_at).toLocaleDateString("en-IN",
                { day: "numeric", month: "long", year: "numeric" })}
            </span>
            {blog.reading_time > 0 && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {blog.reading_time} min read
              </span>
            )}
            {blog.views > 0 && (
              <span className="flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> {blog.views} views
              </span>
            )}
            {blog.author && (
              <span className="flex items-center gap-1.5">
                by <b className="text-neutral-700">{blog.author}</b>
              </span>
            )}
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        {blog.featured_image && (
          <img
            src={blog.featured_image}
            alt={blog.title}
            className="w-full h-72 sm:h-96 object-cover rounded-2xl border border-neutral-200 mb-10"
          />
        )}

        <article
          className="prose prose-neutral max-w-none
            prose-headings:font-heading prose-headings:tracking-tight prose-headings:text-[#111111]
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3
            prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-2
            prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-neutral-700
            prose-strong:text-[#111111]
            prose-a:text-[#7C3AED] prose-a:no-underline hover:prose-a:underline
            prose-ul:my-4 prose-li:my-1 prose-li:text-neutral-700
            prose-code:bg-neutral-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[#7C3AED] prose-code:font-normal
            prose-pre:bg-neutral-900 prose-pre:text-emerald-300 prose-pre:rounded-xl"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
          data-testid="blog-content"
        />

        {blog.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-neutral-200 flex items-center gap-2 flex-wrap">
            <Tag className="w-4 h-4 text-neutral-400" />
            {blog.tags.map((t, i) => (
              <Badge key={i} variant="outline" className="border-neutral-300 text-neutral-600">{t}</Badge>
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-between items-center border-t border-neutral-200 pt-6">
          <Button variant="outline" onClick={handleLike} className="border-neutral-300" data-testid="blog-like-btn">
            <Heart className="w-4 h-4 mr-2" /> Like {blog.likes > 0 && `(${blog.likes})`}
          </Button>
          <Button variant="outline" onClick={handleShare} className="border-neutral-300" data-testid="blog-share-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* CTA to product */}
        <Card className="mt-12 p-8 border-none bg-gradient-to-br from-[#A855F7] to-[#7C3AED] text-white text-center">
          <h3 className="font-heading text-2xl font-semibold">Turn this playbook into pipeline.</h3>
          <p className="text-white/90 text-sm mt-2 max-w-md mx-auto">
            Use 75+ MarkeeMix AI tools to research, write and localise your next campaign in minutes.
          </p>
          <Button
            className="mt-5 bg-white text-[#7C3AED] hover:bg-neutral-100 font-semibold"
            onClick={() => navigate("/tools")}
            data-testid="blog-cta-tools"
          >
            Explore all tools <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>

        {related.length > 0 && (
          <section className="mt-14">
            <h3 className="font-heading text-xl font-semibold text-[#111111] mb-5">Keep reading</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {related.map((r) => (
                <Card
                  key={r.slug}
                  onClick={() => navigate(`/blogs/${r.slug}`)}
                  className="p-5 border-neutral-200 cursor-pointer hover:shadow-md transition-shadow group"
                  data-testid={`related-${r.slug}`}
                >
                  <Badge variant="secondary" className="mb-3 text-[10px]">{r.category}</Badge>
                  <h4 className="font-heading text-base font-semibold text-[#111111] line-clamp-2 group-hover:text-[#7C3AED] transition-colors">
                    {r.title}
                  </h4>
                  {r.excerpt && (
                    <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{r.excerpt}</p>
                  )}
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#7C3AED]">
                    Read <ArrowRight className="w-3 h-3" />
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>

      <PremiumFooter />
    </div>
  );
}
